import Utils from './Utils.js'
import ImageSegmentationProcessor from './ImagePaintProcessing/ImageSegmentationProcessor.js'
import Config from './Config.js'
import { Selector } from './ui/Selectors.js'
import { DOM } from './ui/DOM.js'
import Color from './models/Color.js'
import CXImageAction from './ImagePaintProcessing/CXImageAction.js'
import { TOOL } from './constants/Tools.js'

export default class ToolManager {
    /**
     * @param {Object} deps
     * @param {CanvasState} [deps.canvasState]
     * @param {Object} [deps.project]
     * @param {Document} [deps.document]
     * @param {string} [deps.assetPath]
     */
    constructor({ canvasState, project, document, assetPath } = {}) {
        this._debugId = Math.random().toString(36).slice(2, 8)
        console.log('[ToolManager] ctor id =', this._debugId)

        // Explicit dependency injection keeps this class testable and avoids global lookups.
        this.canvasState = canvasState
        this.project = project
        this.document = document
        this.assetPath = assetPath

        // Initialize all internal state in ONE place
        this._initInternalState()

        // Create segmentation processor
        this.initImageSegmentationProcessor()
    }

    /**
     * Initialize/reinitialize all internal values.
     * This is called by BOTH constructor AND resetForNewProject().
     */
    _initInternalState() {
        // Tools
        this.activeTool = undefined

        // Basic tool parameters
        this.brushSize = 30
        this.drawOnTouchDiametre = ~~(this.brushSize / 2)
        this.opacity = 1.0
        this.diffuser = 0
        this.rotation = 0
        this.scale = 1
        this.brushCursorIcon = 'crosshair'

        // Sliders and buffers
        this.lastSliderWheel = undefined
        this.imgDataSquare = null

        // Luminosity helpers
        this.lum_A = undefined
        this.lum_B = undefined

        // Magic wand
        this.currentMagicAction = null
        this.lastCoordinates = null
        this.isMagicWandProcessing = false

        if (this.imgProObj) {
            this.imgProObj.isProcessing = false
        }
    }

    /**
     * Reset ToolManager for a new project/image.
     * No duplication — just reuse the same init logic.
     */
    resetForNewProject({ canvasState } = {}) {
        const tmId = this._debugId
        console.log(`[ToolManager.resetForNewProject tmId=${tmId}]`)

        if (canvasState) this.canvasState = canvasState

        // Reinitialize internal fields
        this._initInternalState()

        // Recreate segmentation processor using the fresh canvasState
        if (this.canvasState) {
            this.initImageSegmentationProcessor()
        }
    }

    // --- tool switching (existing pattern) ---
    /**
     * Set the active tool identifier.
     * @param {string} toolId
     */
    setActiveTool(toolId) {
        console.log('setActiveTool: ' + toolId)
        this.activeTool = toolId
    }
    /**
     * Get the current active tool identifier.
     * @returns {string|undefined}
     */
    getActiveTool() {
        return this.activeTool
    }

    // --- drawing / selection (moved from Project.js) ---
    /**
     * Paint/erase a circular dab centered on (x, y).
     * This is a 1:1 move from Project.drawOnTouch with minimal edits:
     *  - Reads/writes state from the owning Project to preserve existing behavior
     *  - Uses this.canvasState where appropriate
     */
    drawOnTouch(x, y) {
        const p = this.project
        // Local alias keeps hot-path logic readable and avoids repeated property lookups.
        const cs = this.canvasState || p?.canvasState
        if (!p || !cs) return

        const brushSize = this.brushSize
        const rayon = ~~(brushSize / 2)
        const rayonCarre = rayon * rayon
        const xMoinsRayon = x - rayon
        const yMoinsRayon = y - rayon

        const actual_image = cs.getResultImageImageData(xMoinsRayon, yMoinsRayon, brushSize, brushSize)
        const gray_square = cs.getGrayImageImageData(xMoinsRayon, yMoinsRayon, brushSize, brushSize)
        const original_square = cs.getOriginalImageImageData(xMoinsRayon, yMoinsRayon, brushSize, brushSize)

        this.ensureImgDataSquare(brushSize)

        let a = 0
        let b = 0
        const preX = ~~(y - this.drawOnTouchDiametre)
        const preY = ~~(x - this.drawOnTouchDiametre)
        const image_width = cs._realImageWidth
        const actualData = actual_image.data
        const grayData = gray_square.data
        const originalData = original_square.data
        const outData = this.imgDataSquare.data
        const maskData = cs._maskImage.data
        const lastLevel = cs.actions[cs.actions.length - 1]?.lastLevelpixels?.()
        const workingColorPos = cs.colorSample.workingColorPos

        // Compute LumA and LumB if color is selected
        if (workingColorPos !== 0) {
            this.lum_A = cs.lightningParam[cs.colorSample.getCurrentColor().bleach_value]
            this.lum_B = this.lum_A + 255
        }
        const lumA = this.lum_A
        const lumB = this.lum_B

        if (this.activeTool === TOOL.ERASER) {
            for (let row = 0; row < brushSize; row++) {
                a = preX + row
                const rowOffset = row * brushSize
                for (let col = 0; col < brushSize; col++) {
                    b = preY + col
                    const i = (rowOffset + col) * 4

                    if (b >= preY && b <= image_width) {
                        if (
                            originalData[i] !== actualData[i] ||
                            originalData[i + 1] !== actualData[i + 1] ||
                            originalData[i + 2] !== actualData[i + 2]
                        ) {
                            const dx = b - x
                            const dy = a - y
                            if (dx * dx + dy * dy < rayonCarre) {
                                if (a >= 0 && b >= 0) {
                                    const pos = a * image_width + b
                                    lastLevel?.addPixelwithValue(pos, maskData[pos])
                                    maskData[pos] = Config.DEFAULT_MASKING_COLOR
                                }
                                outData[i] = originalData[i]
                                outData[i + 1] = originalData[i + 1]
                                outData[i + 2] = originalData[i + 2]
                                outData[i + 3] = 255
                            } else {
                                outData[i] = actualData[i]
                                outData[i + 1] = actualData[i + 1]
                                outData[i + 2] = actualData[i + 2]
                                outData[i + 3] = 255
                            }
                        } else {
                            outData[i] = actualData[i]
                            outData[i + 1] = actualData[i + 1]
                            outData[i + 2] = actualData[i + 2]
                            outData[i + 3] = 255
                        }
                    } else {
                        outData[i] = actualData[i]
                        outData[i + 1] = actualData[i + 1]
                        outData[i + 2] = actualData[i + 2]
                        outData[i + 3] = 255
                    }
                }
            }
        } else {
            let colorResult
            const color = cs.colorSample.getCurrentColor()

            const isTextureColor = color.type === Color.TYPE.TEXTURE || color.type === Color.TYPE.TEXTURE_SWATCH
            const texData = isTextureColor ? color.texture_data?.data : null

            // TEXTURE (type 1 or 2)
            if (isTextureColor && texData) {

                for (let row = 0; row < brushSize; row++) {
                    a = preX + row
                    const rowOffset = row * brushSize
                    for (let col = 0; col < brushSize; col++) {
                        b = preY + col
                        const i = (rowOffset + col) * 4

                        if (
                            originalData[i] === actualData[i] &&
                            originalData[i + 1] === actualData[i + 1] &&
                            originalData[i + 2] === actualData[i + 2]
                        ) {
                            const dx = b - x
                            const dy = a - y
                            if (dx * dx + dy * dy < rayonCarre) {
                                const pos = a * image_width + b

                                if (a >= 0 && b >= 0) {
                                    maskData[pos] = workingColorPos
                                    lastLevel?.addPixelwithValue(pos, workingColorPos)
                                }

                                colorResult = Utils.luminosity(
                                    lumA,
                                    lumB,
                                    grayData[i],
                                    texData[pos * 4],
                                    texData[pos * 4 + 1],
                                    texData[pos * 4 + 2],
                                )

                                outData[i] = colorResult[1]
                                outData[i + 1] = colorResult[2]
                                outData[i + 2] = colorResult[3]
                                outData[i + 3] = 255
                            } else {
                                outData[i] = actualData[i]
                                outData[i + 1] = actualData[i + 1]
                                outData[i + 2] = actualData[i + 2]
                                outData[i + 3] = 255
                            }
                        } else {
                            outData[i] = actualData[i]
                            outData[i + 1] = actualData[i + 1]
                            outData[i + 2] = actualData[i + 2]
                            outData[i + 3] = 255
                        }
                    }
                }
            } else {
                // PLAIN COLOR (type 0)
                const swatch = color.swatch_color || {}
                const { sr, sg, sb } = swatch

                for (let row = 0; row < brushSize; row++) {
                    a = preX + row
                    const rowOffset = row * brushSize
                    for (let col = 0; col < brushSize; col++) {
                        b = preY + col
                        const i = (rowOffset + col) * 4

                        if (
                            originalData[i] === actualData[i] &&
                            originalData[i + 1] === actualData[i + 1] &&
                            originalData[i + 2] === actualData[i + 2]
                        ) {
                            const dx = b - x
                            const dy = a - y
                            if (dx * dx + dy * dy < rayonCarre) {
                                if (a >= 0 && b >= 0) {
                                    const pos = a * image_width + b
                                    maskData[pos] = workingColorPos
                                    lastLevel?.addPixelwithValue(pos, workingColorPos)
                                }

                                if (sr != null && sg != null && sb != null) {
                                    const colorResult = Utils.luminosity(lumA, lumB, grayData[i], sr, sg, sb)
                                    outData[i] = colorResult[1]
                                    outData[i + 1] = colorResult[2]
                                    outData[i + 2] = colorResult[3]
                                    outData[i + 3] = 255
                                } else {
                                    outData[i] = actualData[i]
                                    outData[i + 1] = actualData[i + 1]
                                    outData[i + 2] = actualData[i + 2]
                                    outData[i + 3] = 255
                                }
                            } else {
                                outData[i] = actualData[i]
                                outData[i + 1] = actualData[i + 1]
                                outData[i + 2] = actualData[i + 2]
                                outData[i + 3] = 255
                            }
                        } else {
                            outData[i] = actualData[i]
                            outData[i + 1] = actualData[i + 1]
                            outData[i + 2] = actualData[i + 2]
                            outData[i + 3] = 255
                        }
                    }
                }
            }
        }

        cs.viewimage.context.putImageData(
            this.imgDataSquare,
            parseInt(x - this.drawOnTouchDiametre, 10),
            parseInt(y - this.drawOnTouchDiametre, 10),
        )
    }

    /**
     * Slider Opacity
     * Integer value
     */
    sliderOpacity() {
        const ctx = this._getCurrentSwatchContext()
        if (!ctx) return

        const slider = DOM.get(Selector.sliderOpacity)
        if (!slider) return

        const { cs, pos, swatch } = ctx
        const val = parseInt(slider.value, 10)
        if (val === swatch.bleach_value) return

        swatch.bleach_value = val
        cs.refreshSwatchColor(pos)
    }

    /**
     * Slider Diffuser (magic wand sensitivity)
     * Integer value
     *
     * This MUST only do something if a MagicPaint selection already exists.
     * If the user moves the slider before clicking with the magic wand,
     * we just log and bail out.
     * @param {boolean} isMouseUp
     */
    sliderDiffuser(isMouseUp) {
        const paintLevel = parseInt(DOM.get(Selector.sliderWheel).value, 10)

        if (paintLevel === this.lastSliderWheel && !isMouseUp) return
        this.lastSliderWheel = paintLevel

        // If no magic paint action yet, just ignore
        if (!this.currentMagicAction) {
            this.currentMagicAction = this.canvasState.getLastMagicPaintAction()
            if (!this.currentMagicAction) return
        }

        this.computeMagicWand({ paintLevel })
    }

    /**
     * Slider Rotate
     * Integer value
     */
    sliderRotate() {
        const ctx = this._getCurrentSwatchContext()
        if (!ctx) return

        const slider = DOM.get(Selector.sliderRotate)
        if (!slider) return

        const { cs, swatch } = ctx
        const val = parseInt(slider.value, 10)
        if (val === swatch.texture_rotation) return

        swatch.texture_rotation = val
        cs.refreshTextureCanvas()
    }

    /**
     * Slider Scale
     * Float value
     */
    sliderScale() {
        const ctx = this._getCurrentSwatchContext()
        if (!ctx) return

        const slider = DOM.get(Selector.sliderScale)
        if (!slider) return

        const { cs, swatch } = ctx
        const val = parseFloat(slider.value)
        if (val === swatch.texture_scale) return

        swatch.texture_scale = val
        cs.refreshTextureCanvas()
    }

    /**
     * Slider Brush Size
     * Updates brush size and cursor preview.
     * @returns {void}
     */
    sliderBrushSize() {
        console.log('sliderBrushSize')

        const slider = DOM.get(Selector.sliderBrush)
        if (!slider) return

        // Brush size from slider
        const val = parseInt(slider.value, 10)

        // Brush size with ratio
        this.brushSize = ~~(val * this.canvasState._ratioCanvas)
        this.drawOnTouchDiametre = ~~(this.brushSize / 2)

        this.ensureImgDataSquare()

        console.log(this.assetPath + '/img/icons/cursorcircle' + val + '.png')
        // Display image from brush size
        $('#flex-image-size').attr('src', this.assetPath + '/img/icons/cursorcircle' + val + '.png')
    }

    /**
     * Update the brush cursor icon based on the current size.
     * @returns {void}
     */
    sliderBrushCursorIcon() {
        const slider = DOM.get(Selector.sliderBrush)
        if (!slider) return

        // Brush size from slider
        const val = parseInt(slider.value, 10)

        console.log(this.assetPath + '/img/icons/cursorcircle' + val + '.png) ' + ~~(val / 2) + ' ' + ~~(val / 2))
        // Define new cursor
        $(Selector.canvasMain).css({
            cursor: 'url(' + this.assetPath + '/img/icons/cursorcircle' + val + '.png) ' + ~~(val / 2) + ' ' + ~~(val / 2) + ', auto',
        })
    }

    // --- dependency updates (optional, if you already rewire these) ---
    /**
     * Replace the CanvasState reference at runtime.
     * @param {CanvasState} cs
     */
    setCanvasState(cs) {
        this.canvasState = cs
    }
    /**
     * Replace the renderer reference at runtime.
     * @param {Object} r
     */
    setRenderer(r) {
        this.renderer = r
    }
    /**
     * Replace the image processor reference at runtime.
     * @param {Object} p
     */
    setImageProcessor(p) {
        this.imageProcessor = p
    }

    /**
     * Resolve the current swatch context for slider updates.
     * @returns {{cs: CanvasState, pos: number, swatch: Object}|null}
     * @private
     */
    _getCurrentSwatchContext() {
        const cs = this.canvasState
        if (!cs) return null

        const pos = cs.colorSample.workingColorPos
        if (!pos) return null

        const swatch = cs.colorSample.getColorByNumber(pos)
        if (!swatch) return null

        return { cs, pos, swatch }
    }

    // --------------------------------------------------------------------------------------------
    // Magic wand events
    // --------------------------------------------------------------------------------------------

    /**
     * Initialize the image segmentation processor for magic wand operations.
     * @returns {void}
     */
    initImageSegmentationProcessor() {
        console.log('initImageSegmentationProcessor')

        // Init magic wand
        this.imgProObj = new ImageSegmentationProcessor(
            Config.MAGIC_WAND_MAX_LEVEL,
            this.canvasState.getResultImageImageData().data,
            this.canvasState._realImageWidth,
            this.canvasState._realImageHeight,
        )

        // Create empty image gray
        this.imgProObj.createEmptyImageGray()
    }

    /**
     * Magic Wand Pipeline
     *
     * 1) FIRST CALL (coordinates provided):
     *      - create MagicPaint action if missing
     *      - start worker
     *      - compute initial region at paintLevel
     *
     * 2) DIFFUSER UPDATE (no coordinates):
     *      - reuse existing MagicPaint action
     *      - redraw mask based on pixelsPerLevelIndexes
     * @param {Object} [params]
     * @param {number} [params.paintLevel]
     * @param {{x:number,y:number}} [params.coordinates]
     */
    computeMagicWand({ paintLevel, coordinates } = {}) {
        const cs = this.canvasState
        if (!Number.isFinite(paintLevel)) return

        // ------------------------------------------------------------
        // First-call coordinate handling
        // ------------------------------------------------------------
        if (coordinates) {
            this.lastCoordinates = coordinates
        }
        const coords = this.lastCoordinates

        // ------------------------------------------------------------
        // Ensure we have a MagicPaint action
        // ------------------------------------------------------------
        if (!this.currentMagicAction && coords) {
            const action = new CXImageAction()
            action.initWithActionTypeMagicPaint()
            action.setColorSample(cs.colorSample)
            cs.addAction(action)
            this.currentMagicAction = action
        }

        const action = this.currentMagicAction
        if (!action) {
            // Slider used before first click → ignore silently
            return
        }

        // Sync pixel buffer with underlying canvas
        cs.viewimage.loadData()

        // ------------------------------------------------------------
        // FIRST CALL LOGIC
        // ------------------------------------------------------------
        const isFirstCall = action.paintLevel === 0 && coords

        if (isFirstCall) {
            if (this.imgProObj.isProcessing) return

            // 1) Launch worker for segmentation
            this.runImageWorker(coords)

            // 2) UI adjustments
            DOM.show(Selector.sliderLuminosityPanel)
            DOM.hide(Selector.sliderRotatePanel)
            DOM.hide(Selector.sliderScalePanel)
            DOM.hide(Selector.sliderWheelAlt)

            // 3) Feed click image into processor
            this.imgProObj.receivedImageFromClick(coords)

            // 4) Sync UI slider
            const slider = DOM.get(Selector.sliderWheel)
            if (slider) slider.value = paintLevel

            // 5) Save paintLevel on action
            action.paintLevel = paintLevel

            // 6) Paint initial region
            this.imgProObj.drawFirstSurfaceAtPosition(paintLevel).forEach((_, key) => {
                if (cs._maskImage.data[key] === Config.DEFAULT_MASKING_COLOR) {
                    action.lastLevelpixels().addPixelwithValue(key, cs.colorSample.workingColorPos)
                    cs._maskImage.data[key] = cs.colorSample.workingColorPos
                    cs.refreshResultAtPosition([key, cs.colorSample.workingColorPos])
                }
            })

            cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
            return
        }

        // ------------------------------------------------------------
        // DIFFUSER UPDATE LOGIC
        // ------------------------------------------------------------

        // Worker hasn't finished building per-level data yet
        if (!action.pixelsPerLevelIndexes) return

        if (this.imgProObj.isProcessing || this.isMagicWandProcessing) return

        const pixels = action.lastLevelpixels().getPixels()
        const prevLevel = action.paintLevel

        // Remove old level
        const prevCount = action.pixelsPerLevelIndexes[prevLevel] || 0
        for (let i = 0; i < prevCount; i++) {
            const pos = pixels[i][0]
            cs._maskImage.data[pos] = Config.DEFAULT_MASKING_COLOR
            cs.refreshResultAtPosition([pos, Config.DEFAULT_MASKING_COLOR])
        }

        // Add new level
        const newCount = action.pixelsPerLevelIndexes[paintLevel] || 0
        for (let i = 0; i < newCount; i++) {
            const [pos, maskVal] = pixels[i]
            cs._maskImage.data[pos] = maskVal
            cs.refreshResultAtPosition([pos, maskVal])
        }

        cs.viewimage.context.putImageData(cs.viewimage.data, 0, 0)
        action.paintLevel = paintLevel
    }

    /**
     * Web worker -> compute magic wands points
     *
     * After completion :
     *
     * 1. remove old frontier points (maybe not necessary)
     * 2. convert points to dictionnary
     * 3. display sliders depending on selected color / texture
     * @param {{x:number,y:number}} coordinates
     */
    runImageWorker(coordinates) {
        console.log('runImageWorker')

        const cs = this.canvasState
        const action = this.currentMagicAction || cs?.getLastAction()

        if (!cs || !action) {
            console.warn('runImageWorker: no MagicPaint action available')
            return
        }

        if (!this.currentMagicAction) {
            this.currentMagicAction = action
        }

        const worker = new Worker(new URL('./image.worker.js', import.meta.url) /* webpackChunkName: "image.worker" */)

        this.imgProObj.setImageGray(cs._maskImage.data)
        this.imgProObj.isProcessing = true

        worker.postMessage([
            cs._maskImage.data,
            Config.MAGIC_WAND_MAX_LEVEL,
            cs.getResultImageImageData().data,
            cs._realImageWidth,
            cs._realImageHeight,
            coordinates,
        ])

        worker.onmessage = (e) => {
            const results = e.data
            const keysArray = Array.from(results[0].keys())
            const maxIter = keysArray.length

            action.resetLevelpixels()

            for (let i = 0; i < maxIter; i++) {
                action.lastLevelpixels().addPixelwithValue(keysArray[i], cs.colorSample.workingColorPos)
            }

            action.pixelsPerLevelIndexes = results[1]

            const color = cs.colorSample.getCurrentColor()
            if (color.type === 1 || color.type === 2) {
                DOM.show(Selector.sliderLuminosityPanel)
                DOM.show(Selector.sliderRotatePanel)
                DOM.show(Selector.sliderScalePanel)
                DOM.show(Selector.sliderWheelAlt)
            } else {
                DOM.show(Selector.sliderLuminosityPanel)
                DOM.hide(Selector.sliderRotatePanel)
                DOM.hide(Selector.sliderScalePanel)
                DOM.show(Selector.sliderWheelAlt)
            }

            DOM.get(Selector.sliderLuminosityPanel).disabled = false

            this.imgProObj.isProcessing = false
            this.isMagicWandProcessing = false
        }
    }

    /**
     * Ensure `this.imgDataSquare` exists and matches the current brush size.
     * Returns the ImageData so callers can use the value fluently.
     * Handles environments without `ImageData(width, height)` by falling back to a canvas.
     * @param {number} [size=this.brushSize]
     * @returns {ImageData}
     * @private
     */
    ensureImgDataSquare(size = this.brushSize) {
        const needsNew = !this.imgDataSquare || this.imgDataSquare.width !== size || this.imgDataSquare.height !== size

        if (needsNew) {
            // Some older browsers don't expose ImageData(width, height); use a canvas fallback.
            const hasImageDataCtor = typeof ImageData === 'function'
            if (hasImageDataCtor) {
                this.imgDataSquare = new ImageData(size, size)
            } else {
                const c = this.document.createElement('canvas')
                c.width = size
                c.height = size
                this.imgDataSquare = c.getContext('2d', { alpha: false }).createImageData(size, size)
            }
        }
        return this.imgDataSquare
    }
}
